import React, { useMemo, useState } from "react";
import { Form, InputGroup, Modal } from "react-bootstrap";
import {
  ChevronDown,
  ChevronRight,
  Download,
  FileEarmarkBarGraph,
  Search,
  XLg,
} from "react-bootstrap-icons";
import { HeaderButton, CancelButton } from "../../shared";
import { FormattedDate } from "../../../../lib/components/FormattedDate";

function getReportName(report) {
  return (
    report?.friendlyName ||
    report?.FriendlyName ||
    report?.reportName ||
    report?.name ||
    "Report"
  );
}

function getReportFileName(file) {
  return file?.name || file?.fileName || file?.FileName || "Report file";
}

function getReportFileType(report) {
  const hasArchiveFile = Boolean(report?.archiveFileName);
  const outputFormatStr = report?.outputFormat
    ? String(report.outputFormat).toLowerCase()
    : null;
  const fileTypes = Array.isArray(report?.fileTypes)
    ? report.fileTypes.join(", ")
    : report?.fileTypes;
  const fileType = hasArchiveFile ? "zip" : outputFormatStr || fileTypes;
  return fileType || "—";
}

export function PayoutRunReportsModal({
  reports = [],
  runName,
  totalCount,
  onCancel,
  onRequestReport,
  onDownloadReport,
}) {
  const [search, setSearch] = useState("");
  const [downloadingReferenceId, setDownloadingReferenceId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const filteredReports = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matchingReports = query
      ? reports.filter((report) =>
          getReportName(report).toLowerCase().includes(query),
        )
      : reports;

    return [...matchingReports].sort((firstReport, secondReport) => {
      const firstDate = new Date(
        firstReport?.createdAt || firstReport?.requestDate,
      ).getTime();
      const secondDate = new Date(
        secondReport?.createdAt || secondReport?.requestDate,
      ).getTime();

      if (!Number.isFinite(firstDate) && !Number.isFinite(secondDate)) return 0;
      if (!Number.isFinite(firstDate)) return 1;
      if (!Number.isFinite(secondDate)) return -1;
      return secondDate - firstDate;
    });
  }, [reports, search]);

  const toggleExpanded = (key) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleDownloadReport = async (report) => {
    const referenceId = report?.referenceId ?? report?.ReferenceId;
    if (!referenceId || !onDownloadReport || downloadingReferenceId) return;

    setDownloadingReferenceId(referenceId);
    try {
      await onDownloadReport(report);
    } finally {
      setDownloadingReferenceId(null);
    }
  };

  const archiveCount = totalCount ?? reports.length;

  return (
    <Modal
      show
      onHide={onCancel}
      centered
      size="lg"
      className="commission-modal-scope payout-run-reports-modal"
    >
      <Modal.Header className="payout-run-reports-header">
        <div>
          <Modal.Title className="payout-run-reports-title">
            Reports
          </Modal.Title>
          <div className="payout-run-reports-subtitle">
            {runName ? `${runName} · ` : ""}
            {archiveCount} {archiveCount === 1 ? "report" : "reports"}
          </div>
        </div>
        <div className="payout-run-reports-header-actions">
          {onRequestReport && (
            <HeaderButton
              text="Request report"
              variant="outline-primary"
              size="sm"
              icon={<FileEarmarkBarGraph size={14} />}
              onClick={onRequestReport}
            />
          )}
          <button
            type="button"
            className="payout-run-reports-close"
            aria-label="Close modal"
            onClick={onCancel}
          >
            <XLg size={15} />
          </button>
        </div>
      </Modal.Header>

      <div className="payout-run-reports-search-row">
        <InputGroup className="payout-run-reports-search">
          <InputGroup.Text>
            <Search size={13} />
          </InputGroup.Text>
          <Form.Control
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search reports"
          />
        </InputGroup>
      </div>

      <Modal.Body className="p-0">
        {filteredReports.length === 0 ? (
          <div className="payout-run-reports-empty">
            No reports match your search.
          </div>
        ) : (
          <div className="payout-run-reports-list">
            <div className="payout-run-report-row payout-run-report-header">
              <span />
              <span>Report</span>
              <span>Date</span>
              <span>Agent</span>
              <span>File type</span>
              <span>Requested by</span>
              <span>Status</span>
              <span></span>
            </div>
            {filteredReports.map((report, index) => {
              const reportKey =
                report.id ??
                report.reportId ??
                `${getReportName(report)}-${index}`;
              const isExpanded = expandedIds.has(reportKey);
              const files = report?.files ?? [];
              const badgeType = report.badge
                ? String(report.badge).toLowerCase()
                : null;

              return (
                <div className="payout-run-report-group" key={reportKey}>
                  <div className="payout-run-report-row">
                    <button
                      type="button"
                      className="payout-run-report-toggle"
                      onClick={() => toggleExpanded(reportKey)}
                      disabled={files.length === 0}
                      aria-expanded={isExpanded}
                      aria-label={
                        isExpanded ? "Collapse details" : "Expand details"
                      }
                    >
                      {files.length > 0 ? (
                        isExpanded ? (
                          <ChevronDown size={14} />
                        ) : (
                          <ChevronRight size={14} />
                        )
                      ) : (
                        <span className="payout-run-report-toggle-spacer" />
                      )}
                    </button>

                    <div className="payout-run-report-info">
                      <div className="payout-run-report-name">
                        <strong>{getReportName(report)}</strong>
                        {badgeType && (
                          <span
                            className={`payout-run-report-badge payout-run-report-badge--${badgeType}`}
                          >
                            {report.badge.toUpperCase()}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="payout-run-report-date">
                      {(report.createdAt || report.requestDate) && (
                        <FormattedDate
                          date={report.createdAt || report.requestDate}
                          format="MM/DD/YYYY h:mm A"
                        />
                      )}
                    </div>
                    <div className="payout-run-report-agent">
                      {report.agentName || "All Agents"}
                    </div>
                    <div className="payout-run-report-file-type">
                      {getReportFileType(report)}
                    </div>
                    <div className="payout-run-report-author">
                      {report.portalUserName || "—"}
                    </div>
                    <div className="payout-run-report-status">
                      {report.status || "—"}
                    </div>
                    {onDownloadReport ? (
                      <button
                        type="button"
                        className="run-summary-report-download"
                        onClick={() => handleDownloadReport(report)}
                        disabled={
                          !(report?.referenceId ?? report?.ReferenceId) ||
                          String(report?.status || "")
                            .trim()
                            .toLowerCase() !== "success"
                        }
                        aria-label={`Download ${getReportName(report)}`}
                        title={`Download ${getReportName(report)}`}
                      >
                        <Download size={14} />
                      </button>
                    ) : (
                      <span />
                    )}
                  </div>

                  {isExpanded && files.length > 0 && (
                    <div className="payout-run-report-files">
                      <div className="payout-run-report-files-list">
                        {files.map((file) => (
                          <div
                            className="payout-run-report-file-row"
                            key={
                              file.id ??
                              file.commissionReportFileId ??
                              getReportFileName(file)
                            }
                          >
                            <span className="payout-run-report-file-name">
                              {getReportFileName(file)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="payout-run-reports-footer">
        <div className="payout-run-reports-footer-left">
        </div>
        <CancelButton text="Close" onClick={onCancel} size="sm" />
      </Modal.Footer>
    </Modal>
  );
}

export default PayoutRunReportsModal;
